const VIEWPORT_MARGIN = 8;

export interface PopupPlacement {
   align: 'start' | 'center' | 'end';
   side: 'top' | 'bottom';
   sideOffset: number;
   /** At least as wide as the anchor, as a list of options under its field. */
   matchAnchorWidth?: boolean;
}

/**
 * Positions a fixed popup against its anchor, flipping to the roomier side
 * when the preferred one is too short. `--popup-transform-origin` points at
 * the anchor, for a popup that scales in from it.
 *
 * Bounds are the visual viewport: what a phone keyboard or pinch zoom leaves
 * visible. Returns false once the anchor has scrolled out of it.
 */
export function placePopup(
   popup: HTMLElement,
   anchor: HTMLElement,
   { align, side, sideOffset, matchAnchorWidth = false }: PopupPlacement
) {
   const rect = anchor.getBoundingClientRect();
   const viewport = window.visualViewport;
   const top = viewport?.offsetTop ?? 0;
   const bottom = top + (viewport?.height ?? window.innerHeight);
   if (rect.bottom < top || rect.top > bottom) return false;
   const left = viewport?.offsetLeft ?? 0;
   const right = left + (viewport?.width ?? window.innerWidth);

   const maxWidth = right - left - 2 * VIEWPORT_MARGIN;
   if (matchAnchorWidth) popup.style.minWidth = `${Math.min(rect.width, maxWidth)}px`;
   popup.style.maxWidth = `${maxWidth}px`;

   // scrollHeight is the full height whatever max-height is in force, so
   // measuring never resets how far the popup is scrolled.
   const fullHeight = popup.scrollHeight + popup.offsetHeight - popup.clientHeight;
   const below = bottom - rect.bottom - sideOffset - VIEWPORT_MARGIN;
   const above = rect.top - top - sideOffset - VIEWPORT_MARGIN;
   const fitsPreferred = fullHeight <= (side === 'bottom' ? below : above);
   const preferredIsRoomier = side === 'bottom' ? below >= above : above >= below;
   const placedBelow = (side === 'bottom') === (fitsPreferred || preferredIsRoomier);
   const space = Math.max(placedBelow ? below : above, 0);
   const height = Math.min(fullHeight, space);
   popup.style.maxHeight = `${space}px`;
   popup.style.top = `${placedBelow ? rect.bottom + sideOffset : rect.top - sideOffset - height}px`;
   popup.dataset.side = placedBelow ? 'bottom' : 'top';

   const width = popup.offsetWidth;
   const rtl = getComputedStyle(anchor).direction === 'rtl';
   const alignLeft = align === 'start' ? !rtl : align === 'end' ? rtl : null;
   const aligned =
      alignLeft == null
         ? rect.left + (rect.width - width) / 2
         : alignLeft
           ? rect.left
           : rect.right - width;
   const minLeft = left + VIEWPORT_MARGIN;
   const popupLeft = Math.max(minLeft, Math.min(aligned, right - VIEWPORT_MARGIN - width));
   popup.style.left = `${popupLeft}px`;

   const originX = Math.min(Math.max(rect.left + rect.width / 2 - popupLeft, 0), width);
   popup.style.setProperty(
      '--popup-transform-origin',
      `${originX}px ${placedBelow ? 'top' : 'bottom'}`
   );
   return true;
}
